import React from 'react';

import { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { IRoomOption } from 'components/Pages/Admin/Itinerary/Details/RoomsSelect/types';
import { ISelectOption } from 'components/shared/BaseFormComponents/Select/types';
import { ETripEvent } from 'detailsv2/structure';
import { isNotDefined } from 'utils/ramda';

import { CustomSubmitHandler } from '../../../types';

interface PageFieldValues extends FieldValues {
  attachedPage?: ISelectOption;
}

type Props<T extends PageFieldValues> = {
  eventType: ETripEvent;
  methods: UseFormReturn<T>;
  handleSubmit: CustomSubmitHandler;
  fieldValuesHandler?: (fieldValues: FieldValues) => FieldValues;
};

export function useTripEventSaveInfo<T extends PageFieldValues>({
  eventType,
  methods,
  handleSubmit,
  fieldValuesHandler,
}: Props<T>) {
  const { getValues, watch, formState } = methods;
  const attachedPage: PageFieldValues['attachedPage'] = watch('attachedPage' as Path<T>);
  const pageId = attachedPage?.id;
  const pageName = attachedPage?.name;

  const saveInfoSubmitHandler = (isForTeam: boolean) => {
    const fieldValues = fieldValuesHandler ? fieldValuesHandler(getValues()) : getValues();

    if (Array.isArray(fieldValues)) {
      // This case is for flight, there comes an array of data
      handleSubmit(fieldValues, undefined, { eventType, pageId, pageName, wholeTeam: isForTeam });
    } else {
      handleSubmit(
        {
          ...fieldValues,
          data: {
            ...fieldValues.data,
            rooming: fieldValues.data?.rooming?.map((rooming: IRoomOption) => ({
              id: rooming.value,
              name: rooming.label,
              count: rooming.count,
            })),
          },
        },
        undefined,
        {
          eventType,
          pageId,
          pageName,
          wholeTeam: isForTeam,
        },
      );
    }
  };

  const isSafeInfoDisabled = React.useMemo(
    () => isNotDefined(attachedPage) || !formState.isValid,
    [attachedPage, formState.isValid],
  );

  const safeInfoTooltipId = `safe_info_${eventType}_tooltip`;

  return { saveInfoSubmitHandler, isSafeInfoDisabled, safeInfoTooltipId };
}
